import React, { useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppWidget } from '../components/WhatsAppWidget';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Button } from '../components/ui/button';
import {
  Phone,
  Mail,
  MessageCircle,
  Briefcase,
  Award,
  CheckCircle2,
  Building2,
  Film,
  Tv2,
  Megaphone,
  Camera,
  Sparkles,
  ShieldCheck,
  GraduationCap,
  Users,
  Layers,
  ArrowRight,
  ArrowUpRight,
  Zap,
  IndianRupee,
} from 'lucide-react';
import { SITE, getWhatsAppUrl } from '../lib/site';
import { applyPageMeta, buildBreadcrumbSchema } from '../lib/seo';

const HiringPartners = () => {
  useEffect(() => {
    const prev = document.title;
    applyPageMeta({
      title: 'Hire Video Editors Bangalore | MatchFrame Placement Cell',
      description:
        'Hire pre-screened video editors, colorists, motion designers, and post-production talent from MatchFrame Bangalore. 125+ hiring partners, 500+ placed graduates, zero placement fee.',
      keywords:
        'hire video editor Bangalore, hire film editor Bangalore, hire colorist Bangalore, post production hiring Bangalore, video editing recruitment Bangalore, MatchFrame placement, hire editors for production house, Avid editor for hire, Premiere Pro editor Bangalore, DaVinci Resolve colorist Bangalore',
      path: '/hiring-partners',
      schemas: {
        'breadcrumb-jsonld': buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Hiring Partners', path: '/hiring-partners' },
        ]),
      },
    });
    return () => {
      document.title = prev;
    };
  }, []);

  const whatsappUrl = getWhatsAppUrl(SITE.hiringPartnersMessage);

  const partnerLogos = [
    'Film Production Houses',
    'OTT Studios',
    'Post-Production & DI',
    'Ad & Creative Agencies',
    'Marketing Agencies',
    'Wedding Agencies',
    'D2C Brands',
    'Edtech, Fintech & Healthcare',
    'Law Firms',
    'Corporate & Govt Teams',
    'TV & News Channels',
    'Content Creators & YouTubers',
    'Documentary Directors',
    'Podcast Studios',
    'Startups',
    'Creator Agencies',
  ];

  const whyHire = [
    {
      icon: ShieldCheck,
      title: 'Trained to Industry Standard',
      body: 'Our editors graduate on the exact tools studios run: Avid, Premiere Pro, DaVinci Resolve, Final Cut Pro X, After Effects, Photoshop, and modern AI video tools. They walk in job-ready on Day 1, not "learning on the job".',
    },
    {
      icon: CheckCircle2,
      title: 'Independently Pre-Assessed',
      body: 'Every graduate clears graded coursework, practical assignments on real production footage, and a portfolio review by working professionals. You get a screened candidate, not a fresh applicant.',
    },
    {
      icon: Zap,
      title: 'Immediate Availability',
      body: 'We have batches graduating every month with rolling availability across editorial, color, motion graphics, and social-first roles. Interview within a week, hire the same month.',
    },
    {
      icon: IndianRupee,
      title: 'Zero Placement Fee',
      body: 'We do not charge studios a single rupee to hire from us. Our placement cell exists because trust with hiring partners is more valuable than a one-time commission.',
    },
  ];

  const roles = [
    { icon: Film, name: 'Film Editor', kw: 'Avid, Premiere Pro, FCPX. Long-form storytelling and conform.' },
    { icon: Tv2, name: 'TV / OTT Editor', kw: 'Episodic editing, broadcast delivery, dub-ready cuts.' },
    { icon: Sparkles, name: 'Promo & Trailer Editor', kw: 'Pacing, music cutting, branded promos for film and shows.' },
    { icon: Camera, name: 'Post-Production Assistant', kw: 'Ingest, organise, sync, conform. Studio pipeline support.' },
    { icon: Megaphone, name: 'Ad / Branded Content Editor', kw: 'Premiere Pro + DaVinci Resolve workflows for agencies.' },
    { icon: Briefcase, name: 'Social Media Editor', kw: 'Short-form vertical, hooks, captions, fast turnaround.' },
    { icon: Award, name: 'Colorist (DI)', kw: 'DaVinci Resolve. Primary/secondary, node trees, conform and delivery.' },
    { icon: Layers, name: 'Junior Motion Designer', kw: 'After Effects fundamentals for titles, lower thirds, idents.' },
    { icon: Film, name: 'YouTube Channel Editor', kw: 'Creator workflow, narrative cuts, B-roll and engagement edits.' },
    { icon: Building2, name: 'Corporate Video Editor', kw: 'Internal videos, training content, brand films.' },
    { icon: Briefcase, name: 'Podcast Editor', kw: 'Long-form audio editing, multi-cam podcast cutdowns.' },
    { icon: Sparkles, name: 'AI Video Specialist', kw: 'AI-assisted video generation integrated with traditional pipeline.' },
  ];

  const software = [
    'Avid Media Composer',
    'Adobe Premiere Pro',
    'Final Cut Pro X',
    'DaVinci Resolve',
    'After Effects (Basics)',
    'Photoshop (Basics)',
    'AI Video Creation Tools',
    'Meta Ads (Targeting & Distribution)',
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Share your requirement',
      body: 'A 30-second message on WhatsApp, email, or a call. Tell us the role, software stack, and timeline.',
    },
    {
      step: '02',
      title: 'We shortlist from the cohort',
      body: 'Our placement cell matches your brief to graduating / placed students based on skills, portfolio, and fit.',
    },
    {
      step: '03',
      title: 'Direct interviews, no middlemen',
      body: 'You interview the candidates directly. We send the brief, portfolio links, and trainer notes ahead of the call.',
    },
    {
      step: '04',
      title: 'Trial / portfolio review',
      body: 'Optional paid trial edit or extended portfolio review. Confirm fit before you commit.',
    },
    {
      step: '05',
      title: 'Extend the offer',
      body: 'You make the offer directly to the candidate. No commissions. No placement fees. We just stay in the loop for any future requirements.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <AnnouncementBar />
      <Header
        onConsultationClick={() => {
          const el = document.getElementById('partner-contact');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }}
      />

      {/* HERO */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 via-slate-900 to-purple-900/40 z-0"></div>
        <div className="absolute top-20 right-10 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-5xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs uppercase tracking-widest font-bold text-orange-300 mb-6">
              <Briefcase className="h-4 w-4" /> For Hiring Partners
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
              Our trained video editors are hired by
              <span className="block bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent mt-2">
                125+ hiring partners.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-3xl">
              MatchFrame has placed{' '}
              <span className="text-white font-semibold">500+</span> graduates across film and TV production, OTT, advertising, marketing and wedding agencies, D2C brands, edtech, fintech, healthcare, law firms, corporates, governments, TV and news channels, documentary teams, creator studios, and startups. <span className="text-white font-semibold">Zero placement fee</span> to the hiring partner.
            </p>

            <div className="flex flex-wrap gap-3">
              <a href="#partner-contact" data-testid="hp-hero-primary-cta">
                <Button className="bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white px-8 py-6 text-base rounded-full font-bold shadow-xl">
                  Start Hiring
                  <ArrowUpRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="hp-hero-whatsapp-cta">
                <Button variant="outline" className="bg-white/5 border-white/20 hover:bg-white/10 text-white px-8 py-6 text-base rounded-full">
                  <MessageCircle className="mr-2 h-5 w-5 text-emerald-400" />
                  WhatsApp the Placement Cell
                </Button>
              </a>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-5xl">
            {[
              ['750+', 'Students Trained'],
              ['500+', 'Students Placed'],
              ['125+', 'Hiring Partners'],
              ['25+', 'Years of Training Experience'],
            ].map(([num, label]) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent">
                  {num}
                </div>
                <div className="text-sm text-gray-400 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIRING PARTNER TYPE WALL */}
      <section className="py-16 bg-slate-900 border-t border-white/5">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Who hires from MatchFrame
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Across film, TV, OTT, advertising, brands, governments, content teams, and startups. Anyone hiring full-time editing talent.
              </p>
            </div>

            {/* Marquee-style wall */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {partnerLogos.map((label) => (
                <div
                  key={label}
                  className="bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 rounded-xl px-4 py-5 text-center text-sm font-semibold text-gray-200 transition-all"
                >
                  {label}
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-sm mt-8 italic">
              ...and almost every team in India hiring visual storytellers full-time.
            </p>
          </div>
        </div>
      </section>

      {/* WHY HIRE FROM US */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <span className="inline-block px-3 py-1 mb-4 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-widest font-bold rounded-full">
                Why hire from us
              </span>
              <h2 className="text-3xl md:text-5xl font-bold leading-tight max-w-3xl">
                A hiring pipeline built for studios that
                <span className="block bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent mt-2">
                  don&apos;t have time to train juniors.
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {whyHire.map((item) => (
                <div
                  key={item.title}
                  className="group bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-orange-400/40 rounded-2xl p-7 transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-600 to-pink-600 flex items-center justify-center">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{item.title}</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO HIRE */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12 text-center">
              <span className="inline-block px-3 py-1 mb-4 bg-purple-500/10 text-purple-300 text-xs uppercase tracking-widest font-bold rounded-full">
                How to Hire
              </span>
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                Here&apos;s how you can hire from us
              </h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                A simple 5-step process. Most studios go from first message to extending an offer in under 2 weeks.
              </p>
            </div>

            <div className="relative">
              {/* connecting line */}
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-purple-500/50 via-orange-500/50 to-pink-500/50 hidden md:block"></div>

              <div className="space-y-6">
                {howItWorks.map((s, i) => (
                  <div
                    key={s.step}
                    className={`relative flex flex-col md:flex-row gap-6 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex md:justify-end">
                      <div className={`bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-purple-400/30 rounded-2xl p-7 max-w-md transition-all ${i % 2 === 0 ? 'md:mr-10' : 'md:ml-10'}`}>
                        <div className="text-5xl font-black bg-gradient-to-br from-orange-400 to-pink-500 bg-clip-text text-transparent leading-none mb-3">
                          {s.step}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{s.body}</p>
                      </div>
                    </div>
                    {/* center dot */}
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-7">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-500 to-pink-600 ring-4 ring-slate-950"></div>
                    </div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center mt-12">
              <a href="#partner-contact" data-testid="hp-howto-cta">
                <Button className="bg-gradient-to-r from-purple-600 to-orange-600 hover:from-purple-700 hover:to-orange-700 text-white px-10 py-6 text-lg rounded-full font-bold shadow-xl">
                  Send Your Hiring Brief
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <span className="inline-block px-3 py-1 mb-4 bg-emerald-500/10 text-emerald-300 text-xs uppercase tracking-widest font-bold rounded-full">
              Talent Pool
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Roles we train and place for</h2>
            <p className="text-gray-300 text-lg mb-10 max-w-3xl">
              Our students graduate ready to step into a working studio on Day 1. Here are the roles our hiring partners regularly fill from MatchFrame:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((r) => (
                <div
                  key={r.name}
                  className="bg-white/5 border border-white/10 hover:border-orange-400/40 rounded-2xl p-5 transition-all"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-600 to-pink-600 flex items-center justify-center">
                      <r.icon className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-white">{r.name}</h3>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{r.kw}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOFTWARE STACK */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Software our graduates know</h2>
            <p className="text-gray-300 text-lg mb-10 max-w-3xl">
              We train on industry-standard tooling. By the time a MatchFrame student walks into your studio, they&apos;ve already shipped real projects on these tools.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {software.map((s) => (
                <div key={s} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-white font-medium">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATION PROCESS */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">How students get certified</h2>
            <p className="text-gray-300 text-lg mb-10 max-w-3xl">
              Our certification isn&apos;t a participation trophy. Students earn it through assessed coursework, practical assignments on real footage, and portfolio reviews. Here&apos;s how the pipeline works end-to-end:
            </p>

            <div className="space-y-5">
              {[
                {
                  icon: GraduationCap,
                  title: '1. Structured curriculum on industry footage',
                  body: 'Each program is broken into modules: editorial, color, motion, audio, finishing. Students learn each module on professional, production-grade footage, not stock clips.',
                },
                {
                  icon: Users,
                  title: '2. Small batch sizes with 1-on-1 mentoring',
                  body: 'Class sizes are deliberately small. Trainers, many of them award-winning working editors, review individual timelines, give frame-by-frame feedback, and rebuild bad cuts with the student until the instinct sticks.',
                },
                {
                  icon: CheckCircle2,
                  title: '3. Assessment tests and portfolio review',
                  body: 'Before certification, students complete graded assessments and a portfolio piece reviewed by a panel of working professionals. Only students who clear both walk out with our certificate.',
                },
                {
                  icon: Briefcase,
                  title: '4. Internship + studio exposure (Professional Diploma)',
                  body: 'The Professional Diploma adds a 2-month studio internship on real production work. Students leave with an official experience certificate on company letterhead and a portfolio of shipped work.',
                },
                {
                  icon: ShieldCheck,
                  title: '5. Direct introduction to hiring partners',
                  body: 'Graduates are introduced to our hiring partner network with context: what they trained on, what they shipped, where they are strongest. You get a screened candidate; they get a real interview instead of a cold application.',
                },
              ].map((step) => (
                <div key={step.title} className="flex gap-5 bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] transition-all">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-orange-600 flex items-center justify-center">
                    <step.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT BLOCK */}
      <section id="partner-contact" className="py-20 bg-gradient-to-br from-orange-900/30 via-slate-950 to-purple-900/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto bg-white/[0.04] border border-white/10 rounded-3xl p-8 md:p-12 backdrop-blur-md">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Hire the best video talent now.
              </h2>
              <p className="text-gray-300 max-w-2xl mx-auto text-lg">
                Tell us the role, the software stack, and the timeline. We&apos;ll come back with a shortlist of pre-screened candidates, usually within a few days. Zero placement fees.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <a href={`tel:${SITE.phone}`} data-testid="hp-contact-call" className="block">
                <div className="bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-orange-400/40 rounded-2xl p-6 text-center transition-all h-full">
                  <Phone className="h-7 w-7 text-orange-400 mx-auto mb-3" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Call</div>
                  <div className="font-bold text-white">{SITE.phoneDisplay}</div>
                </div>
              </a>
              <a
                href={`mailto:${SITE.email}?subject=${encodeURIComponent('Hiring from MatchFrame - Open Role')}`}
                data-testid="hp-contact-email"
                className="block"
              >
                <div className="bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-purple-400/40 rounded-2xl p-6 text-center transition-all h-full">
                  <Mail className="h-7 w-7 text-purple-300 mx-auto mb-3" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Email</div>
                  <div className="font-bold text-white text-sm break-all">{SITE.email}</div>
                </div>
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="hp-contact-whatsapp" className="block">
                <div className="bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-emerald-400/40 rounded-2xl p-6 text-center transition-all h-full">
                  <MessageCircle className="h-7 w-7 text-emerald-400 mx-auto mb-3" />
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">WhatsApp</div>
                  <div className="font-bold text-white">Send a message</div>
                </div>
              </a>
            </div>

            <div className="text-center mt-10">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="hp-contact-primary-cta">
                <Button className="bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white px-10 py-6 text-lg rounded-full font-bold shadow-xl">
                  Start Hiring Today
                  <ArrowUpRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default HiringPartners;
